import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@ais-ui/ui-core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { StepperFooter } from './StepperFooter';
import { StepperHeadline } from './StepperHeadline';
import React, { useEffect, useMemo } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../../../conversions/useDateFormat';
import { useCreateReportContext } from './CreateReportContext';
import {
  createReportTemplatePeriodFormSchema,
  reportTemplatePeriodValues,
} from '../utils/createPeriodFormSchema';
import { DateTimePickerField } from '../../../utils/DatetimePickerField';

export const PeriodStep = () => {
  const { messages } = useLocalization();

  const {
    stepperProps,
    dateRange,
    setDateRange,
    startDatetimeThreshold,
    endDatetimeThreshold,
  } = useCreateReportContext();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const [hasInitiatedValues, setHasInitiatedValues] = React.useState(false);

  const formSchema = useMemo(
    () =>
      createReportTemplatePeriodFormSchema(
        startDatetimeThreshold,
        endDatetimeThreshold,
        formatDateTimeWithTimeZone(startDatetimeThreshold),
        formatDateTimeWithTimeZone(endDatetimeThreshold)
      ),
    [startDatetimeThreshold, endDatetimeThreshold, formatDateTimeWithTimeZone]
  );

  const form = useForm<reportTemplatePeriodValues>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      start_datetime: dateRange?.from || new Date(),
      end_datetime: dateRange?.to || new Date(),
    },
  });

  useEffect(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const defaultStart = new Date(yesterday);
    defaultStart.setHours(0, 0, 0, 0);

    const defaultEnd = new Date(yesterday);
    defaultEnd.setHours(23, 59, 59, 999);

    form.reset({
      start_datetime: dateRange?.from || defaultStart,
      end_datetime: dateRange?.to || defaultEnd,
    });

    setHasInitiatedValues(true);
  }, [dateRange, form]);

  const onPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    stepperProps.prevStep();
  };

  const onNext = (data: reportTemplatePeriodValues) => {
    setDateRange({
      from: data.start_datetime,
      to: data.end_datetime,
    });
    stepperProps.nextStep();
  };

  return (
    <>
      <StepperHeadline
        headerText={messages.reportsCreateReportPeriodTitle}
        subHeaderText={messages.reportsCreateReportPeriodSubtitle}
      />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onNext)}
          className="flex flex-col space-y-8 pt-4 h-full flex-grow"
        >
          <div className="h-[85%]">
            {hasInitiatedValues && (
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="start_datetime"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>
                        {messages.reportsCreateReportStartDateLabel ||
                          'Start Date'}{' '}
                        *
                      </FormLabel>
                      <FormControl>
                        <DateTimePickerField
                          value={field.value}
                          onChange={(value) => {
                            field.onChange(value);
                          }}
                          dateLimit="past"
                          startDatetimeThreshold={startDatetimeThreshold}
                          endDatetimeThreshold={endDatetimeThreshold}
                          className="w-80"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="end_datetime"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>
                        {messages.reportsCreateReportEndDateLabel || 'End Date'}{' '}
                        *
                      </FormLabel>
                      <FormControl>
                        <DateTimePickerField
                          value={field.value}
                          onChange={(value) => {
                            field.onChange(value);
                          }}
                          dateLimit="past"
                          startDatetimeThreshold={startDatetimeThreshold}
                          endDatetimeThreshold={endDatetimeThreshold}
                          className="w-80"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
          <StepperFooter
            onPrevious={onPrevious}
            onNext={form.handleSubmit(onNext)}
            stepperProps={stepperProps}
          />
        </form>
      </Form>
    </>
  );
};
